import { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { formatToCustomLocale } from "../../utilities";
import {
  selectFreeRoundsCompletionPending,
  selectFreeRoundsStatus,
  selectFreeRoundsTotalWin,
  selectCurrency,
  selectSpinComplete,
  selectCounterAnimationFinished,
  selectWinAnimationsPlaying,
  selectCurrentRound,
  selectInfoPopupActive,
  selectErrorPopupActive,
  selectHoldAndWinActive,
  selectShowHoldAndWinOutro,
  selectIsFreeRoundsCompletionReload,
  selectBalanceAlreadyUpdated,
  selectIntroPopupActive,
  selectAutoPlayActive,
  selectBalance,
  selectFreeRoundsCount,
  selectFreeRoundsPerformed,
  selectHoldAndWinCurrentRound,
} from "../../State/selectors";
import {
  openInfoPopup,
  openErrorPopup,
  setBet,
  stopAutoPlay,
  setBalance,
} from "../../State/actions";
import {
  setCompletionPending,
  clearCompletionPending,
  setFreeRoundsEnded,
  clearFreeRoundsEnded,
  clearFreeRoundsCompletionReload,
  clearReloadPending,
} from "../../State/Slices/freeRoundsSlice";
import { clearFeatureStateToBase } from "../../State/Slices/gameSettingsSlice";
import store from "../../State/Store/store";
import StorageManager from "../../utils/StorageManager";

/**
 * FreeRoundsCompletionHandler - Handles free rounds completion flow
 *
 * Waits for all animations to complete before showing popups:
 * 1. Detects when completionPending flag is set
 * 2. Waits for reels to stop (spinComplete)
 * 3. For winning rounds: waits for counter and big win animations
 * 4. For non-winning rounds: adds 1000ms delay
 * 5. Shows info popup with total win
 * 6. After info dismissed, shows warning popup
 * 7. After warning dismissed, updates balance and clears state
 */
const FreeRoundsCompletionHandler = () => {
  const dispatch = useDispatch();

  const completionPending = useSelector(selectFreeRoundsCompletionPending);
  const freeRoundsStatus = useSelector(selectFreeRoundsStatus);
  const freeRoundsTotalWin = useSelector(selectFreeRoundsTotalWin);
  const currency = useSelector(selectCurrency);

  const spinComplete = useSelector(selectSpinComplete);
  const counterAnimationFinished = useSelector(selectCounterAnimationFinished);
  const winAnimationsPlaying = useSelector(selectWinAnimationsPlaying);
  const currentRound = useSelector(selectCurrentRound);

  const infoPopupActive = useSelector(selectInfoPopupActive);
  const errorPopupActive = useSelector(selectErrorPopupActive);

  const isHoldAndWinActive = useSelector(selectHoldAndWinActive);
  const showHoldAndWinOutro = useSelector(selectShowHoldAndWinOutro);
  const isFreeRoundsCompletionReload = useSelector(
    selectIsFreeRoundsCompletionReload,
  );
  const balanceAlreadyUpdated = useSelector(selectBalanceAlreadyUpdated);
  const introPopupActive = useSelector(selectIntroPopupActive);
  const autoPlayActive = useSelector(selectAutoPlayActive);
  const currentBalance = useSelector(selectBalance);
  const freeRoundsCount = useSelector(selectFreeRoundsCount);
  const freeRoundsPerformed = useSelector(selectFreeRoundsPerformed);
  const holdAndWinCurrentRound = useSelector(selectHoldAndWinCurrentRound);

  const [completionPhase, setCompletionPhase] = useState(null);
  const timeoutIdRef = useRef(null);
  const emergencyTimeoutRef = useRef(null);
  const wasInfoPopupActiveRef = useRef(false);
  const wasErrorPopupActiveRef = useRef(false);
  const previousStatusRef = useRef(null);

  // Detect status change from "active" to "played" and trigger completion flow
  // CRITICAL: Only trigger if ALL free rounds are actually completed (performed === count)
  // Do NOT trigger if H&W just completed mid-free-rounds (more rounds left to play)
  useEffect(() => {
    const wasActive = previousStatusRef.current === "active";
    const nowPlayed = freeRoundsStatus === "played";

    previousStatusRef.current = freeRoundsStatus;

    // Check if free rounds are actually ending (all rounds played)
    const allRoundsPlayed = freeRoundsPerformed === freeRoundsCount;

    if (wasActive && nowPlayed && !completionPending && allRoundsPlayed) {
      // NOTE: Bet restoration now happens at RGS level (loadGameState)
      // When free rounds complete, RGS returns betToRestore as the bet
      // This ensures bet is always correct at state management level
      dispatch(setCompletionPending());
    }
  }, [freeRoundsStatus, completionPending, freeRoundsPerformed, freeRoundsCount, dispatch]);

  // Wait for animations to complete before showing completion popup
  useEffect(() => {
    if (!completionPending || completionPhase !== null) {
      return;
    }

    // Check if last round was H&W (has H&W win but no regular round win)
    const wasHoldAndWinRound = holdAndWinCurrentRound?.win > 0;
    const hasRegularWin = currentRound?.win > 0 && !wasHoldAndWinRound;

    if (isHoldAndWinActive || showHoldAndWinOutro) {
      return;
    }

    if (isFreeRoundsCompletionReload) {
      if (introPopupActive) {
        return;
      }
      setCompletionPhase("ready_to_show");
      return;
    }

    let animationsComplete = false;
    if (hasRegularWin) {
      // Regular win: wait for counter and win animations
      animationsComplete =
        spinComplete && counterAnimationFinished && !winAnimationsPlaying;
    } else if (wasHoldAndWinRound) {
      // H&W round: just wait for spin complete (counter already played during H&W outro)
      animationsComplete = spinComplete;
    } else {
      // No win: add small delay
      if (spinComplete && timeoutIdRef.current === null) {
        const id = setTimeout(() => {
          timeoutIdRef.current = null;
          setCompletionPhase("ready_to_show");
        }, 100);
        timeoutIdRef.current = id;
        return;
      }
    }

    if (animationsComplete) {
      setCompletionPhase("ready_to_show");
    }
  }, [
    completionPending,
    completionPhase,
    isFreeRoundsCompletionReload,
    introPopupActive,
    spinComplete,
    counterAnimationFinished,
    winAnimationsPlaying,
    currentRound,
    holdAndWinCurrentRound,
    isHoldAndWinActive,
    showHoldAndWinOutro,
  ]);

  useEffect(() => {
    if (completionPhase === "ready_to_show") {
      dispatch(setFreeRoundsEnded());

      // Stop autoplay if it was active during free rounds
      if (autoPlayActive) {
        dispatch(stopAutoPlay());
      }

      // CRITICAL: Restore bet to base game bet (before free rounds)
      // Use betToRestore from storage (saved when free rounds started)
      const savedFreeRound = StorageManager.loadFreeRound();
      const savedBaseGame = StorageManager.loadBaseGameRound();
      const betToRestore = savedFreeRound?.betToRestore ?? savedBaseGame?.bet;
      const currentBets = store.getState().game.bets;

      if (betToRestore && currentBets.includes(betToRestore)) {
        dispatch(setBet(betToRestore));
      }

      // CRITICAL: Update balance with Free Rounds total win (natural flow only)
      // Skip if: (1) reload scenario, OR (2) balance already updated from H&W outro + refreshGameData
      if (!isFreeRoundsCompletionReload && !balanceAlreadyUpdated) {
        const newBalance = currentBalance + freeRoundsTotalWin;
        dispatch(setBalance(newBalance));
      }

      const formattedTotalWin = formatToCustomLocale(freeRoundsTotalWin);

      dispatch(
        openInfoPopup({
          key: "you_have_won_in_free_rounds",
          data: { N: `${formattedTotalWin} ${currency.toUpperCase()}` },
        }),
      );

      setCompletionPhase("waiting_for_info");

      emergencyTimeoutRef.current = setTimeout(() => {
        dispatch(clearCompletionPending());
        dispatch(clearFreeRoundsCompletionReload());
        dispatch(clearFreeRoundsEnded());
        dispatch(clearFeatureStateToBase());
        setCompletionPhase(null);
      }, 30000);
    }
  }, [completionPhase, freeRoundsTotalWin, currency, autoPlayActive, isFreeRoundsCompletionReload, balanceAlreadyUpdated, currentBalance, dispatch]);

  useEffect(() => {
    if (infoPopupActive) {
      wasInfoPopupActiveRef.current = true;
    }

    if (
      completionPhase === "waiting_for_info" &&
      wasInfoPopupActiveRef.current &&
      !infoPopupActive
    ) {
      wasInfoPopupActiveRef.current = false;

      // CRITICAL: Balance updates are handled by WinDisplay.jsx ONLY
      // API already includes Free Rounds win in the balance
      // WinDisplay will sync displayedBalance when it becomes visible

      dispatch(openErrorPopup("206"));
      setCompletionPhase("waiting_for_warning");
    }
  }, [
    infoPopupActive,
    completionPhase,
    freeRoundsTotalWin,
    isFreeRoundsCompletionReload,
    dispatch,
  ]);

  useEffect(() => {
    if (errorPopupActive) {
      wasErrorPopupActiveRef.current = true;
    }

    if (
      completionPhase === "waiting_for_warning" &&
      wasErrorPopupActiveRef.current &&
      !errorPopupActive
    ) {
      wasErrorPopupActiveRef.current = false;

      if (emergencyTimeoutRef.current) {
        clearTimeout(emergencyTimeoutRef.current);
        emergencyTimeoutRef.current = null;
      }

      // Bet was already restored when status changed to "played"
      // Clear completion flags but keep status="played" until next spin
      dispatch(clearCompletionPending());
      dispatch(clearFreeRoundsCompletionReload());
      dispatch(clearFreeRoundsEnded());
      dispatch(clearReloadPending());
      dispatch(clearFeatureStateToBase()); // Next spin must be base game, not free rounds

      setCompletionPhase(null);
    }
  }, [errorPopupActive, completionPhase, dispatch]);

  useEffect(() => {
    return () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
      if (emergencyTimeoutRef.current) {
        clearTimeout(emergencyTimeoutRef.current);
      }
    };
  }, []);

  return null;
};

export default FreeRoundsCompletionHandler;
